import React from 'react';
import type { monaco } from 'react-monaco-editor';
import useMounted from 'Hooks/useMounted';
import { MonacoYaml, configureMonacoYaml } from 'monaco-yaml';
import { EditorProps, YamlValidationOptions } from '../EditorV3';
import { getTextModelOptions } from './useEditorLanguage';

interface UseEditorYamlValidationProps {
  yamlValidationOptions?: YamlValidationOptions;
  monacoInstance: typeof monaco;
  editor: monaco.editor.IStandaloneCodeEditor;
  language: EditorProps['language'];
  value: EditorProps['value'];
}

const useEditorYamlValidation = ({
  yamlValidationOptions,
  monacoInstance,
  language,
  editor,
  value,
}: UseEditorYamlValidationProps) => {
  const isMounted = useMounted();
  const monacoYamlRef = React.useRef<monaco.editor.ITextModel | null>(null);
  const monacoYamlUpdaterRef = React.useRef<MonacoYaml | null>(null);

  React.useEffect(() => {
    if (yamlValidationOptions && monacoInstance && editor && isMounted.current) {
      if (language !== 'yaml') {
        throw new Error(
          'Invalid language specified. Yaml Validation options should only be used with `yaml` language.',
        );
      }
      const modelUri = editor.getModel().uri;
      monacoYamlUpdaterRef.current = configureMonacoYaml(monacoInstance);

      // Create the model that will be associated with monaco-yaml
      const existingModel = monacoInstance.editor.getModel(modelUri);
      const model = existingModel || monacoInstance.editor.createModel(value, language, modelUri);
      model.setValue(value);
      model.updateOptions(getTextModelOptions(language));
      editor.setModel(model);
      monacoYamlRef.current = model;
    }

    // Clean up
    return () => {
      monacoYamlRef.current?.dispose();
    };

    // We don't want this effect to run every time the `value` changes.
    // We only need the value for initialization purposes. We don't want to re-initialize the model
    // evert time the `value` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, isMounted, language, monacoInstance]);

  React.useEffect(() => {
    if (!monacoYamlUpdaterRef.current || !editor || !yamlValidationOptions) {
      return;
    }
    const modelUri = editor.getModel().uri;
    monacoYamlUpdaterRef.current.update({
      schemas: [
        {
          // Associate with our model
          fileMatch: [String(modelUri)],
          uri: yamlValidationOptions.docUri,
          schema: yamlValidationOptions.schema,
        },
      ],
    });
  }, [language, monacoYamlUpdaterRef, yamlValidationOptions, editor]);
};

export default useEditorYamlValidation;
